/**
 * Bulk-import wizard (super_admin). Paste or upload a CSV of past help → dry-run preview
 * (per-row validation, errors surfaced) → commit, which seeds the Served wall with
 * closed, public historical cases.
 */
import { useState } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, Play } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import styles from './BulkImport.module.css';

const SAMPLE = `name,phone,village,block,category,amount,summary,date
Gurmail Singh,9876543210,Kotkapura,Kotkapura,housing,35000,Roof repair before monsoon,2026-02-15
Pargat Kaur,9876543211,Jaitu,Jaitu,ration,4500,One month of rations,2026-03-01`;

export default function BulkImport() {
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(reader.result);
      setPreview(null);
    };
    reader.readAsText(file);
  };

  const dryRun = async () => {
    setBusy(true);
    try {
      const res = await api.post('/cases/import/dry-run', { csv });
      setPreview(res);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await api.post('/cases/import', { csv });
      setDone(res);
      toast.success(`Imported ${res.created} cases to the Served wall.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={styles.page}>
        <div className={styles.doneCard}>
          <CheckCircle2 size={48} className={styles.doneIcon} />
          <h1>Import complete</h1>
          <p><strong>{done.created}</strong> cases added to the Served wall · <strong>{done.skipped}</strong> rows skipped.</p>
          <button className={styles.primary} onClick={() => { setDone(null); setPreview(null); setCsv(''); }}>Import another file</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <p className="eyebrow">Reporting</p>
        <h1 className={styles.h1}>Bulk import past help</h1>
        <p className={styles.lede}>Seed the Served wall with help already given. Columns: name, phone, village, block, category, amount, summary, date.</p>
      </header>

      <div className={styles.card}>
        <div className={styles.uploadRow}>
          <label className={styles.uploadBtn}>
            <Upload size={16} /> Upload CSV
            <input type="file" accept=".csv,text/csv" hidden onChange={(e) => onFile(e.target.files[0])} />
          </label>
          <button className={styles.sample} onClick={() => { setCsv(SAMPLE); setPreview(null); }}>Use sample</button>
        </div>
        <textarea
          className={styles.textarea}
          rows={8}
          placeholder="Paste CSV here, or upload a file…"
          value={csv}
          onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
        />
        <div className={styles.actions}>
          <button className={styles.secondary} onClick={dryRun} disabled={!csv.trim() || busy}>
            <Play size={15} /> {busy ? 'Validating…' : 'Dry-run preview'}
          </button>
          {preview && preview.valid > 0 && (
            <button className={styles.primary} onClick={commit} disabled={busy}>
              Import {preview.valid} valid rows <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div className={styles.card}>
          <div className={styles.previewHead}>
            <FileSpreadsheet size={18} />
            <h3>Preview</h3>
            <span className={styles.tagOk}><CheckCircle2 size={13} /> {preview.valid} valid</span>
            {preview.invalid > 0 && <span className={styles.tagBad}><AlertTriangle size={13} /> {preview.invalid} invalid</span>}
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Line</th><th>Name</th><th>Village</th><th>Category</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr key={r.line} className={r.valid ? '' : styles.invalidRow}>
                    <td className="mono">{r.line}</td>
                    <td>{r.data.name || '—'}</td>
                    <td>{r.data.village || '—'}</td>
                    <td>{r.data.category || '—'}</td>
                    <td className="mono">{r.data.amount || '—'}</td>
                    <td>
                      {r.valid ? (
                        <span className={styles.ok}><CheckCircle2 size={13} /> Valid</span>
                      ) : (
                        <span className={styles.bad} title={r.errors.join(', ')}><AlertTriangle size={13} /> {r.errors[0]}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
